/**
 * DemocratCharts — replay hotkeys.
 *
 *   R           toggle replay mode on/off
 *   Space       play / pause (when replay active)
 *   ←  →        step backward / forward
 *   + / =       increase speed
 *   - / _       decrease speed
 *   B           bookmark current candle (legacy; now M to avoid conflict with Buy)
 *   M           bookmark current candle
 *   Esc         exit replay mode
 *
 * Disabled when an input is focused so it doesn't fight with form fields.
 */

import { useEffect } from "react";
import { useReplayStore } from "@/store/replay";

interface UseReplayHotkeysParams {
  /** Called when user presses B (we need symbol/exchange/timeframe context from the page). */
  onBookmark?: () => void;
}

export function useReplayHotkeys({ onBookmark }: UseReplayHotkeysParams = {}) {
  const {
    active,
    enter,
    exit,
    togglePlay,
    stepForward,
    stepBackward,
    bumpSpeedUp,
    bumpSpeedDown,
  } = useReplayStore();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Don't hijack typing in form fields
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isEditable =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        (e.target as HTMLElement)?.isContentEditable;
      if (isEditable) return;

      // Don't compete with browser shortcuts
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case "r":
        case "R":
          e.preventDefault();
          active ? exit() : enter();
          break;
        case " ":
          if (active) {
            e.preventDefault();
            togglePlay();
          }
          break;
        case "ArrowRight":
          if (active) {
            e.preventDefault();
            stepForward();
          }
          break;
        case "ArrowLeft":
          if (active) {
            e.preventDefault();
            stepBackward();
          }
          break;
        case "+":
        case "=":
          if (active) {
            e.preventDefault();
            bumpSpeedUp();
          }
          break;
        case "-":
        case "_":
          if (active) {
            e.preventDefault();
            bumpSpeedDown();
          }
          break;
        case "m":
        case "M":
          if (active && onBookmark) {
            e.preventDefault();
            onBookmark();
          }
          break;
        case "Escape":
          if (active) {
            e.preventDefault();
            exit();
          }
          break;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [active, enter, exit, togglePlay, stepForward, stepBackward, bumpSpeedUp, bumpSpeedDown, onBookmark]);
}
